import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    Typography as RebaseTypography,
    cls,
    defaultBorderMixin,
    surfaceHoverMixin,
    iconSize
} from "@rebasepro/ui";
import {
    Paperclip as RebasePaperclipIcon,
    // `AlertTriangle`, not `TriangleAlert` — see the note in EmailComposer.tsx.
    // Only the old spelling resolves in both lucide versions this repo builds
    // against.
    AlertTriangle as RebaseWarnIcon
} from "lucide-react";

import {
    buildSnippet,
    compareMessages,
    fullStamp,
    hasQuotedReply,
    readerDocument,
    sinceLabel,
    stripQuotedReply,
    STATUS_LABEL,
    type MessageRow
} from "./_email_shared";

const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const PaperclipIcon = RebasePaperclipIcon as React.ComponentType<any>;
const WarnIcon = RebaseWarnIcon as React.ComponentType<any>;

/**
 * One conversation, read the way Gmail reads one.
 *
 * ── Oldest first, and only the last one open ─────────────────────────
 *
 * Both halves of that matter, and this screen only had the first. A thread of
 * eleven messages opened on the message from three weeks ago, at full height,
 * with the reply that arrived this morning below the fold — so the answer to
 * "what did they say?" was: scroll. Gmail solved this a long time ago and the
 * solution is not "newest first", which breaks the thread of an argument;
 * it is that the history collapses to one line each and the newest message is
 * the one that is open.
 *
 * A collapsed message shows what a Gmail one shows: who wrote, the first words
 * of it, and when. Clicking opens it in place. "Ver los N anteriores" opens
 * all of them at once, for the times you are looking for something rather than
 * catching up.
 *
 * ── How a stranger's HTML gets on screen ─────────────────────────────
 *
 * Two layers, and both are load-bearing.
 *
 * The first ran on the server: `body_html` was reduced to an inert subset of
 * HTML before it was stored, so the dangerous form is not at rest in the
 * database (services/email-sanitize.ts).
 *
 * The second is here. The markup goes into an **iframe with `sandbox` set to
 * nothing at all** — no `allow-scripts`, no `allow-same-origin`. Those two
 * absences mean a script cannot run, and that even if one somehow did, the
 * document is in an opaque origin with no access to this panel's cookies,
 * storage, DOM or session. `srcDoc` also carries a `Content-Security-Policy`
 * that blocks every remote fetch, so a tracking pixel the sanitizer missed still
 * does not tell a sender when a recruiter opened their message.
 *
 * `dangerouslySetInnerHTML` would have been half the code and is the reason
 * 33e0a3b8 exists. Not here, and not on this input in particular: this is the
 * only surface in the product whose content is written by anyone on the internet
 * who knows an email address.
 *
 * ── Why plain text is the default ────────────────────────────────────
 *
 * Almost every message worth reading here is a person writing a paragraph, and
 * the text version of it is shorter, faster, and identical in meaning. The HTML
 * is one click away for the ones where the layout is the message.
 */

/** How tall the HTML frame gets before it scrolls on its own. */
const FRAME_MAX_HEIGHT = 520;

export function EmailThreadView({ messages, client }: { messages: MessageRow[]; client: any }) {
    const ordered = useMemo(() => [...messages].sort(compareMessages), [messages]);
    const newestId = ordered.length > 0 ? ordered[ordered.length - 1].id : null;

    /**
     * The messages the reader has opened by hand.
     *
     * Reset when the conversation changes — keyed on the newest message's id,
     * which is the cheapest thing that is different for every thread. Without
     * the reset, opening the third message of one conversation opens whatever
     * happens to be third in the next one.
     */
    const [opened, setOpened] = useState<string[]>([]);
    useEffect(() => { setOpened([]); }, [newestId]);

    /**
     * The middle of a long thread, behind one bar.
     *
     * Collapsing the history to one line each was not enough on its own: a
     * conversation of eleven still pushed the newest message a screen and a
     * half down, which is the complaint this whole file is answering. So the
     * shape is Gmail's — how it starts, a stack for everything in between,
     * and what they just said — and only threads long enough to need it get
     * a stack. Four messages fit; nine do not.
     *
     * Every hook in this component runs before the first `return`, including
     * this one. It used to sit further down, under the empty-thread guard, and
     * that is a hooks-order crash (React #310) waiting for the one render where
     * a thread arrives empty — a conversation still loading, or one whose
     * messages were all filtered out — and then arrives full: two hooks on the
     * first render, four on the second.
     */
    const HEAD = 1;
    const TAIL = 2;
    const stackable = ordered.length - HEAD - TAIL;
    const [stackOpen, setStackOpen] = useState(false);
    useEffect(() => { setStackOpen(false); }, [newestId]);
    const hidden = stackable >= 2 && !stackOpen
        ? new Set(ordered.slice(HEAD, HEAD + stackable).map(m => m.id))
        : new Set<string>();

    if (ordered.length === 0) {
        return (
            <Typography variant="body1" color="secondary">
                Esta conversación no tiene mensajes todavía.
            </Typography>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {ordered.map((message, index) => {
                if (hidden.has(message.id)) {
                    // One bar for the whole middle, drawn where the first of
                    // them would have been.
                    return message.id === ordered[HEAD].id ? (
                        <button
                            key="stack"
                            type="button"
                            onClick={() => setStackOpen(true)}
                            className={cls(
                                "self-start px-3 py-1.5 rounded-full border text-sm",
                                surfaceHoverMixin,
                                defaultBorderMixin
                            )}
                        >
                            ··· {hidden.size} mensajes anteriores
                        </button>
                    ) : null;
                }
                // The newest is always open: it is the reason the thread was
                // opened. Everything else waits to be asked for.
                const isOpen = index === ordered.length - 1 || opened.includes(message.id);
                return isOpen
                    ? <EmailMessageCard key={message.id} message={message} client={client} />
                    : (
                        <CollapsedMessage
                            key={message.id}
                            message={message}
                            onOpen={() => setOpened(ids => [...ids, message.id])}
                        />
                    );
            })}
        </div>
    );
}

/** Who wrote a message, as the collapsed line and the open card both say it. */
function senderOf(message: MessageRow): string {
    return message.direction === "inbound"
        ? (message.from_name || message.from_email || "Alguien")
        : "SustenTalent";
}

/**
 * One line of history: who, the first words, when.
 *
 * A button rather than a div with a click handler, so it is reachable by
 * keyboard and announced as something that can be opened — this is the only
 * way to read the middle of a long thread.
 */
function CollapsedMessage({ message, onOpen }: { message: MessageRow; onOpen: () => void }) {
    const preview = (message.body_snippet ?? "").trim() || buildSnippet(message.body_text);
    return (
        <button
            type="button"
            onClick={onOpen}
            className={cls(
                "w-full text-left px-3 py-2 rounded border flex items-baseline gap-2 min-w-0",
                surfaceHoverMixin,
                defaultBorderMixin
            )}
        >
            <span className="text-sm font-medium shrink-0 max-w-[40%] truncate">{senderOf(message)}</span>
            <span className="text-sm opacity-60 truncate grow">{preview}</span>
            <span className="text-xs opacity-60 shrink-0">{sinceLabel(message.sent_at)}</span>
        </button>
    );
}

function EmailMessageCard({ message, client }: { message: MessageRow; client: any }) {
    const inbound = message.direction === "inbound";
    const [showHtml, setShowHtml] = useState(false);
    const [showQuoted, setShowQuoted] = useState(false);
    const [downloading, setDownloading] = useState<string | null>(null);
    const [attachError, setAttachError] = useState("");

    /**
     * Fetch one attachment and hand it to the browser.
     *
     * Through `fetch` with the auth header rather than a plain `<a href>`,
     * because the endpoint is admin-only and a link cannot carry an
     * Authorization header — the alternative is a token in the URL, which then
     * lives in history and in any log that records query strings.
     *
     * The object URL is revoked straight after: it pins the whole file in memory
     * until it is, and a CV-heavy thread would otherwise hold every attachment
     * anybody clicked for as long as the tab is open.
     */
    const download = useCallback(async (name: string) => {
        setDownloading(name);
        setAttachError("");
        try {
            const token = await client?.resolveToken?.();
            const base = `${client?.baseUrl ?? ""}${client?.apiPath ?? "/api"}`;
            const url = `${base}/functions/attachment?message=${encodeURIComponent(message.id)}&name=${encodeURIComponent(name)}`;
            const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok) {
                const json = await res.json().catch(() => ({}));
                throw new Error((json as any)?.error || `Error ${res.status}`);
            }
            const blob = await res.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
        } catch (err: any) {
            setAttachError(err?.message || "No se pudo abrir el adjunto");
        } finally {
            setDownloading(null);
        }
    }, [client, message.id]);

    const text = (message.body_text ?? "").trim();
    // Computed from the body, never from `body_snippet`.
    //
    // `body_snippet` is the *inbox list's* preview: quote-stripped, then
    // whitespace-collapsed, then cut to 180 characters. Using it as the reader's
    // body — which this did — truncated every message longer than a short
    // paragraph mid-sentence and flattened its paragraphs into one line, behind
    // a button that claimed to be about quoted history. `hasQuotedReply` answers
    // the question actually being asked, and says no when there is nothing to
    // reveal.
    const hasQuoted = hasQuotedReply(text);
    const shown = hasQuoted && !showQuoted ? stripQuotedReply(text) : text;

    const status = (message.status ?? "received") as keyof typeof STATUS_LABEL;
    const attachments = (message.attachment_names ?? []).filter(Boolean);

    return (
        <Card className={cls(
            "p-3 flex flex-col gap-2",
            // The one visual cue that says which way a message went, before any
            // label is read. Ours is indented and tinted; theirs sits flush left.
            // A solid, not an alpha fill: the Card underneath is opaque, and
            // `surface-hover` would replace that background rather than tint it.
            !inbound && "ml-6 bg-surface-raised"
        )}>
            <div className="flex items-start gap-2 flex-wrap">
                <div className="grow min-w-0">
                    <Typography variant="body2" className="font-medium truncate">
                        {senderOf(message)}
                        {inbound && message.from_name && message.from_email && (
                            <span className="ml-2 text-xs font-normal font-mono text-text-secondary dark:text-text-secondary-dark">
                                {message.from_email}
                            </span>
                        )}
                    </Typography>
                    <Typography variant="body2" color="secondary" className="truncate">
                        {fullStamp(message.sent_at)}
                        {(message.to_emails ?? []).length > 0 && ` · para ${(message.to_emails ?? []).join(", ")}`}
                        {(message.cc_emails ?? []).length > 0 && ` · cc ${(message.cc_emails ?? []).join(", ")}`}
                        {message.actor && !inbound && ` · lo envió ${message.actor}`}
                    </Typography>
                </div>
                <Chip size="small" colorScheme={
                    status === "failed" ? "redLighter" : inbound ? "greenLighter" : "blueLighter"
                }>
                    {STATUS_LABEL[status] ?? status}
                </Chip>
            </div>

            {/*
                A send that failed keeps its text, so the operator can copy it
                into a new attempt rather than rewrite it. The reason is shown
                because "no salió" without one is not something anybody can act
                on.
            */}
            {status === "failed" && message.error && (
                <div className="flex items-start gap-1.5">
                    <WarnIcon size={iconSize.smallest} className="mt-0.5 shrink-0" />
                    <Typography variant="body2" color="secondary">
                        No se pudo enviar: {message.error}
                    </Typography>
                </div>
            )}

            {shown && (
                <Typography variant="body1" className="whitespace-pre-wrap break-words">
                    {shown}
                </Typography>
            )}

            <div className="flex items-center gap-3 flex-wrap">
                {hasQuoted && (
                    <Button size="small" variant="text" onClick={() => setShowQuoted(v => !v)}>
                        {showQuoted ? "Ocultar lo citado" : "Mostrar el mensaje completo"}
                    </Button>
                )}
                {message.body_html && (
                    <Button size="small" variant="text" onClick={() => setShowHtml(v => !v)}>
                        {showHtml ? "Ver como texto" : "Ver con formato"}
                    </Button>
                )}
                {attachments.map(name => (
                    <Button
                        key={name}
                        size="small"
                        variant="text"
                        disabled={downloading === name}
                        onClick={() => download(name)}
                    >
                        <PaperclipIcon size={12} />
                        {downloading === name ? `Abriendo ${name}…` : name}
                    </Button>
                ))}
            </div>

            {attachError && (
                <Typography variant="body2" color="secondary">
                    {attachError}
                </Typography>
            )}

            {showHtml && message.body_html && (
                <div className={cls("border rounded overflow-hidden", defaultBorderMixin)}>
                    <iframe
                        // No `allow-scripts` and no `allow-same-origin`. Both
                        // absences matter and neither is an oversight — see the
                        // note at the top of this file.
                        sandbox=""
                        referrerPolicy="no-referrer"
                        title="Mensaje con formato"
                        srcDoc={readerDocument(message.body_html)}
                        style={{ width: "100%", height: FRAME_MAX_HEIGHT, border: 0, background: "#ffffff" }}
                    />
                </div>
            )}
        </Card>
    );
}
